// Official BCM hand-drawn shapes, supplied by BCM leadership as SVG files.
//
// RULES
// - Every shape is used EXACTLY as supplied: never redrawn, recolored, or
//   stretched. The color families below are separate supplied files.
// - There is no pink set. If BCM supplies one, add a `pink` family here and it
//   will flow into every scatter automatically.
//
// Shapes are grouped by visual weight so a scatter can mix categories:
//   strokes  — underlines, squiggles, arrows (light, linear)
//   stars    — stars, sparkles, asterisks (small, punchy)
//   organics — blobs, flowers, geometric shapes (larger, solid)

export type DoodleFamily = 'lightBlue' | 'navy' | 'yellow' | 'cream'
export type DoodleCategory = 'strokes' | 'stars' | 'organics'

const cdn = 'https://cdn.magicpatterns.com/uploads'

export const doodleCatalog: Record<DoodleFamily, Record<DoodleCategory, string[]>> = {
  lightBlue: {
    strokes: [
      `${cdn}/ne9j9zvXKDME7TufwPzfEk/Vector-70.svg`,
      `${cdn}/vntzz6yCP5Cr25AzQVBS55/Vector-71.svg`,
      `${cdn}/j4ELnccUexPntXjZRmKCsm/Vector-74.svg`,
      `${cdn}/wh5VZpBYYoSubRgX7rezWA/Vector-76.svg`,
      `${cdn}/dzCjWJREPTpPmNT35hvp36/Vector-77.svg`,
      `${cdn}/9tFafPgeBVfkvgjxKens42/Vector-78.svg`,
      `${cdn}/8UcR1SBnQJDaav9JWkxtcu/Vector-79.svg`,
      `${cdn}/36oFPyZ4eFiJeYS3Up1Z8C/Vector-63.svg`,
      `${cdn}/gPnhGM8wafqbNimsoxz7qi/Vector-64.svg`,
      `${cdn}/gxBxo6SUznFkW2advJev3a/Vector-65.svg`,
      `${cdn}/csZszyASUJC7ipfRSssThh/Vector-66.svg`,
      `${cdn}/3GRUmwJcwCGEFbvJBM3v5e/Vector-67.svg`,
      `${cdn}/tZEZYruKQBRFEYUqgZu31u/Vector-68.svg`,
      `${cdn}/uycpvRmBAkSVamtD8yRCzL/Vector-69.svg`,
      `${cdn}/cWbqbrsg9a7vzaEvaktiMa/Union-3.svg`,
    ],
    stars: [
      `${cdn}/kpicCTXtJNDV59nLXiahYh/Vector-60.svg`,
      `${cdn}/hDS9ABJNsDP5kzuQe5Dyx6/Vector-61.svg`,
      `${cdn}/eDDa9yB47BMMwRqYZ3HXqw/Vector-62.svg`,
      `${cdn}/3H9HpsUtASF43TvpCbhtea/Vector-46.svg`,
      `${cdn}/mkVuLv6JyBwNtQPCcgZDXS/Vector-47.svg`,
      `${cdn}/v1TwrMDWtLNuNR3aMDptCL/Vector-48.svg`,
      `${cdn}/1tDPrNozuHnHYhZRrteapJ/Vector-49.svg`,
      `${cdn}/oT5wPUr2QkJW9M5phys1Nw/Vector-50.svg`,
      `${cdn}/dpHyK3nQTemKMq5oDZtFPz/Vector-51.svg`,
      `${cdn}/s5gAt2LycR1UcNDG8BfiLC/Vector-52.svg`,
      `${cdn}/roU77hSXaa5tDxNDbHZuDw/Vector-53.svg`,
      `${cdn}/vDTGhx9swfvyGgLXSX9roK/Vector-54.svg`,
      `${cdn}/x3gzX7hUYUHCGMJJMp4mbb/Vector-55.svg`,
      `${cdn}/b9VXfcufesUHK6BScPZaNt/Vector-56.svg`,
      `${cdn}/xo6JWKXem16JQqJEkPgLEP/Vector-57.svg`,
      `${cdn}/vXUnZPHqgACMfSZ4YiJ3tY/Vector-58.svg`,
      `${cdn}/6ACtLasZrZAFS8SifWxF5D/Vector-59.svg`,
      `${cdn}/opxZLxhykD5io3BH8roM9u/Vector-25.svg`,
      `${cdn}/9TzQibZ2hAPMPj94sCrKmj/Vector-27.svg`,
      `${cdn}/wytrWg8EYwoSwUFT4DoGKu/Vector-32.svg`,
      `${cdn}/dK4Mub854wTwmMdGTDod5r/Vector-37.svg`,
      `${cdn}/gPiQpGTd5QnpRrnZKyWYVa/Vector-38.svg`,
      `${cdn}/jLK8nwGYYXFXmDFdMN1Uw9/Subtract-2.svg`,
      `${cdn}/8HT81ziRUPCyAbGmZy1n6y/Union-1.svg`,
      `${cdn}/jgQxNdJsgue7YnBaGhkhuo/Union-2.svg`,
      `${cdn}/rQfrhbKs1pnFY8ZszHnWzS/Union.svg`,
    ],
    organics: [
      `${cdn}/uW2xkiqU7zMnqrRcFrpuDr/Vector-72.svg`,
      `${cdn}/ksqPNZVzbgccaYANbKhj45/Vector-73.svg`,
      `${cdn}/tFGvxEtT4r6eqnSmkA6yix/Vector-75.svg`,
      `${cdn}/8n7zVRAs6YJc87B1gE1yAJ/Vector-40.svg`,
      `${cdn}/muLzZwtez1BjAA2FN6QRGX/Vector-41.svg`,
      `${cdn}/cSzQ6gcftKhAi1DY6XWV7M/Vector-42.svg`,
      `${cdn}/8FtWLQrovtt3GBn7f1Hivy/Vector-43.svg`,
      `${cdn}/5hYV7r4NrKGxQHWH9QPJ9L/Vector-44.svg`,
      `${cdn}/tXuZmQbwWKDYuUwkmAj2Ma/Vector-45.svg`,
      `${cdn}/9qAdRBET34G2efXaLc7SSh/Vector-23.svg`,
      `${cdn}/2hVTozzQmPqMbX4aGkZuGZ/Vector-24.svg`,
      `${cdn}/qfaUA8pXxE7PJCA7u5Myhz/Vector-26.svg`,
      `${cdn}/gbFLqvpVpSENPPDYCAVPnH/Vector-28.svg`,
      `${cdn}/kUdbyddQUoVEhw1P5cxht5/Vector-34.svg`,
      `${cdn}/nwbmPrJGrYuA3i22y6wkQb/Vector-35.svg`,
      `${cdn}/ujsFE48CbEtf3hyo4EaAX5/Vector-5.svg`,
      `${cdn}/vGjCXM5KHLia9Z7ZoVbSAb/Vector-7.svg`,
      `${cdn}/hApYvpmbsi4Ckfh2ZxZ686/Vector-12.svg`,
      `${cdn}/9Y7sv655pxmBAxzwzhh84n/Vector-13.svg`,
      `${cdn}/ff4vAUeQxUiytPh6K2G4Q2/Vector-15.svg`,
      `${cdn}/nB4VSuTb2cE3wqKpArK46j/Vector-17.svg`,
      `${cdn}/1BF4344f9MRR9iGAcwN2Dj/Vector-19.svg`,
      `${cdn}/7DRGYGfiqBcTSKj9bXeTVi/Subtract-1.svg`,
      `${cdn}/7N65CJHXBnpYrRCyNdcRfn/Union-4.svg`,
    ],
  },
  navy: {
    strokes: [
      `${cdn}/sfHz7uwoyJwCdbDuwJJmiS/Vector-70.svg`,
      `${cdn}/2Sp3JKcV1sFC9rcZVirkkd/Vector-71.svg`,
      `${cdn}/5ybWCYQ6qNgxTqYFiCyrah/Vector-74.svg`,
      `${cdn}/rxqhQEC3H85kQF5hkxfcis/Vector-76.svg`,
      `${cdn}/d66mQToyxCqNtWJFRsmzzg/Vector-77.svg`,
      `${cdn}/5odwKzagd9KCCkDjDVCJwV/Vector-78.svg`,
      `${cdn}/byzq4bM8dZVvFwQBPZ9omT/Vector-79.svg`,
      `${cdn}/qbB1SnEZU6Y472DXp3c3WN/Vector-63.svg`,
      `${cdn}/iX2cNeAxnCgot9ka1fU7V7/Vector-64.svg`,
      `${cdn}/1PLC2sw1CwPbDtMLhfVQnU/Vector-65.svg`,
      `${cdn}/onoYqp6NSgVJxiKwJBCAbG/Vector-66.svg`,
      `${cdn}/t4R2fcvWZT375bL2KqXxyk/Vector-67.svg`,
      `${cdn}/qVZPXCXN2bYhjzdLBY3muh/Vector-68.svg`,
      `${cdn}/dFUCSJ3A5U3SZBeLduZ8UL/Vector-69.svg`,
      `${cdn}/4Besa8pNK8Bk59eSJvSRzQ/Union-3.svg`,
    ],
    stars: [
      `${cdn}/wJw3esi32yn5uC7yh5vizK/Vector-50.svg`,
      `${cdn}/vTLNSoq3vzaHQvJNh9r4Vi/Vector-51.svg`,
      `${cdn}/sp7FgJq7ERBuGMDMiHYU5r/Vector-52.svg`,
      `${cdn}/cCPdgykiNWLHVwfQ5b17uw/Vector-53.svg`,
      `${cdn}/9mJFKn2yW5uLtib8EAmoQH/Vector-54.svg`,
      `${cdn}/i5A32cin2w3eWpC2RwjV1o/Vector-55.svg`,
      `${cdn}/txAxvPKN7L2yBmyYaJqJak/Vector-56.svg`,
      `${cdn}/52JfaU7PG3DbRc9WQ7NkG3/Vector-57.svg`,
      `${cdn}/oarpHGXTHXFgiGxPj8WFGp/Vector-58.svg`,
      `${cdn}/mPiJYip2r89ynheZoEbjoW/Vector-59.svg`,
      `${cdn}/48mD7QXzX48eypx99VcEsR/Vector-60.svg`,
      `${cdn}/kUTojFdj9MH5vYJbeEFKSk/Vector-61.svg`,
      `${cdn}/hbQMmLth225YGwiZVfCJfX/Vector-62.svg`,
      `${cdn}/uhD2AAJzgFuj51cXX97Bo9/Vector-46.svg`,
      `${cdn}/tcDmVPnUnQ8ZNE6HDq7497/Vector-47.svg`,
      `${cdn}/rYb6Fuc9TBWUcV3ThKP1zr/Vector-48.svg`,
      `${cdn}/n5hVeoQLCZa8BVXFFYM5bq/Vector-49.svg`,
      `${cdn}/qCtSk49VN3j9g5EdrtURVE/Vector-32.svg`,
      `${cdn}/9d6UcGo175HQ9Lj79T6kDs/Vector-37.svg`,
      `${cdn}/vRKdpPQKui6hda8YSBHyc2/Vector-38.svg`,
      `${cdn}/rc3zzLtXVvJTZMZ4eXqBdN/Vector-25.svg`,
      `${cdn}/4Uy52m8iz3NtndCXMSNwQW/Vector-27.svg`,
      `${cdn}/dRNmYm8cJSctB2m8B29KMk/Subtract-2.svg`,
      `${cdn}/oSsGn1zQiS54QZJCeQAC1W/Union-1.svg`,
      `${cdn}/fKpoU29XxpGfYHnSTg1mPj/Union-2.svg`,
      `${cdn}/8ybCuyN22pmURikadyub6w/Union.svg`,
    ],
    organics: [
      `${cdn}/rBfnvpwbYUDrCoeMbzUbeK/Vector-72.svg`,
      `${cdn}/ga2q2wamGPwzDG12M6pkow/Vector-73.svg`,
      `${cdn}/jLFLRuvD58UQKhGkzoDw7P/Vector-75.svg`,
      `${cdn}/t5EWpDA9yEiibD4B6YxEPu/Vector-40.svg`,
      `${cdn}/pe9n3wSLTAChRXBr3My6zg/Vector-41.svg`,
      `${cdn}/hLu7VPnYBosdQJdgEuTuqx/Vector-42.svg`,
      `${cdn}/u2YNVYeSAxHZQButRH4xSJ/Vector-43.svg`,
      `${cdn}/eV6fdx5ut31amV9oRsWL62/Vector-44.svg`,
      `${cdn}/mEvmp32MEp6XxWaG5puYK7/Vector-45.svg`,
      `${cdn}/7MbLnkUS3oHfrMVmEUaQrM/Vector-23.svg`,
      `${cdn}/uDEu2PRwAWFYJ7W25JXQn2/Vector-24.svg`,
      `${cdn}/7Ge7MaSqaYFtLAcUeHvYJV/Vector-26.svg`,
      `${cdn}/iYSrm8Vko8Ne7jb7cohyce/Vector-28.svg`,
      `${cdn}/xnciJt8rkYvnnPHsqRdnj9/Vector-34.svg`,
      `${cdn}/iPxdfetk5oEsV9PKEN9mgy/Vector-35.svg`,
      `${cdn}/5WiwpMiNbKzvQHLo3T7SSM/Vector-5.svg`,
      `${cdn}/n9RXwEw7HynoLASacqxfcE/Vector-7.svg`,
      `${cdn}/2g4RSxJTfb2EFgBXru7Sgg/Vector-12.svg`,
      `${cdn}/dQPC5hfZTzo1K8U77yQ6fc/Vector-13.svg`,
      `${cdn}/jTGia9X1xZ65maLrVhfh6A/Vector-15.svg`,
      `${cdn}/fpqKDuQTF2HYXB7NUpehta/Vector-17.svg`,
      `${cdn}/5jBRXy6h3cWwuPdzgSvGNh/Vector-19.svg`,
      `${cdn}/tRDgD5zrzZ1C1wgcwPe7dr/Subtract-1.svg`,
      `${cdn}/g5wP3GVKJsFNF1jwaEjGz7/Union-4.svg`,
    ],
  },
  yellow: {
    strokes: [
      `${cdn}/1zYDThmvoZ1dcFgyhQzW8c/Vector-70.svg`,
      `${cdn}/fFbZn37E2uHuthz7Tj2fgP/Vector-71.svg`,
      `${cdn}/joEtFZuxaj9vAFm8R7X7qs/Vector-74.svg`,
      `${cdn}/oN9pC5DPrWjntgG5EgDP2a/Vector-76.svg`,
      `${cdn}/9m3mo5yNQdzn5KNeSYf5sW/Vector-77.svg`,
      `${cdn}/5R7XweNSspg7jamxRPGS2X/Vector-78.svg`,
      `${cdn}/mrwsKM7VzBq86q28VgH77n/Vector-79.svg`,
      `${cdn}/cnb518MEvXkX1Mg4x8s46c/Vector-63.svg`,
      `${cdn}/3tab5KUaEUb4drPNZjrEcy/Vector-64.svg`,
      `${cdn}/5EGd5jFfQS96oDqgDxK5p2/Vector-65.svg`,
      `${cdn}/iaBNTKUXzQw4butujARpEE/Vector-66.svg`,
      `${cdn}/qk566AQ8vtaNLrcTxbuW7R/Vector-67.svg`,
      `${cdn}/v5GkQVrbvn9TcWMvFbasV3/Vector-68.svg`,
      `${cdn}/9qMjs9FhRwrEzwDCz4Pvr4/Vector-69.svg`,
      `${cdn}/2XHfo1pKW9thgyfLyHX7Cv/Union-3.svg`,
    ],
    stars: [
      `${cdn}/87JdJWGuJRSybQGSDuCCrR/Vector-61.svg`,
      `${cdn}/rQE32rzXn9uh9jTbYw3nNG/Vector-62.svg`,
      `${cdn}/phA8yazbmfoM6CJwsXX9f7/Vector-46.svg`,
      `${cdn}/nuJF2v17dEknmcd6h5W431/Vector-47.svg`,
      `${cdn}/hsAA73xx3siaXgrkvXHSda/Vector-48.svg`,
      `${cdn}/ogcbmiz5ouVyvUedx3SzBp/Vector-49.svg`,
      `${cdn}/8catkRDMz811URsNip1MXM/Vector-50.svg`,
      `${cdn}/5zJ2Qcja6zm3YWx8b4Hk4R/Vector-51.svg`,
      `${cdn}/2hxJwJPZXFYe6Aw68Aw5EA/Vector-52.svg`,
      `${cdn}/8CA5y5UGvP3GvwWCZ3qDWo/Vector-53.svg`,
      `${cdn}/sMQeTSSMT5NbgZKWPjrkdz/Vector-54.svg`,
      `${cdn}/upoEJDYjHv5hA5rwDjbNNZ/Vector-55.svg`,
      `${cdn}/xgg86edCXsNKNtR9WdyEkL/Vector-56.svg`,
      `${cdn}/stMSoAeAEC7nzKcscYDwat/Vector-57.svg`,
      `${cdn}/sZCDZnuih9NZpaBHedgh36/Vector-58.svg`,
      `${cdn}/5SL7JP5yLX9sAWxFGAxWJt/Vector-59.svg`,
      `${cdn}/3nmroDGHFqitkkXkda1EWA/Vector-60.svg`,
      `${cdn}/e1aKgqUTeY8ZXpkGx1fXze/Union-1.svg`,
      `${cdn}/8pdJ8xjkvzCkTM7dCV4tqz/Union-2.svg`,
      `${cdn}/oRbbGD5z1GiivBoQRxmwKE/Union.svg`,
      `${cdn}/2kNsDSAZbqR8UwoaMH9DQz/Subtract-2.svg`,
    ],
    organics: [
      `${cdn}/c5Yxuifyxvybw53sroVYdb/Vector-72.svg`,
      `${cdn}/bcaz4AADkCh6HTQyEcJ4nU/Vector-73.svg`,
      `${cdn}/dxcoGXYVmWWpUa6WPw3keD/Vector-75.svg`,
      `${cdn}/tLdiwbCM48Lk31DejimF9M/Vector-41.svg`,
      `${cdn}/3JTNX69onn8gBHbaUp3E7X/Vector-42.svg`,
      `${cdn}/1gD4p2zP7EhnqSfKDmdm2D/Vector-43.svg`,
      `${cdn}/nivURzxD2KASo5Cbjca7xf/Vector-44.svg`,
      `${cdn}/gwq3abRhVuYY69uW1T7EYH/Vector-45.svg`,
      `${cdn}/aCgyY5GbbAcaahWZJScGEW/Vector-1.svg`,
      `${cdn}/eroJYzrBC65s7iwoh2GGDR/Vector-2.svg`,
      `${cdn}/oBnmHV8q17XAbyDHKoBRCR/Vector-4.svg`,
      `${cdn}/uL4mdsPobjTXWjh6LNcENi/Vector-5.svg`,
      `${cdn}/wd48xXqLcRpqoXeRn1YXv7/Vector-7.svg`,
      `${cdn}/bkJhy68VnptDQaN4xqXH54/Vector-12.svg`,
      `${cdn}/eVNQsrAdvzoRHbY9UkqCCe/Vector-13.svg`,
      `${cdn}/emS5Tnn8Mr3JMjJnk3prLj/Vector-15.svg`,
      `${cdn}/5wVAuUvqzyqgU2A918LqAJ/Vector-17.svg`,
      `${cdn}/wTYqUwXvCXMsrT3xRk4d8s/Vector-19.svg`,
      `${cdn}/9Xc21EonMyxK1hiPQfRMAM/Subtract-1.svg`,
      `${cdn}/nAhfcMTj5Zu3ZJaLwpqghc/Union-4.svg`,
      `${cdn}/4jrwEhJqurKCWg1viHqFPR/Vector.svg`,
    ],
  },
  cream: {
    strokes: [
      `${cdn}/eTZquSPvFUqMw49uUEUtC4/Vector-70.svg`,
      `${cdn}/pZsjDVJRD6QtzheD5DDJLD/Vector-71.svg`,
      `${cdn}/rsDcPpseBcKVVhfLaMhrcw/Vector-74.svg`,
      `${cdn}/3woctDDuxdXo93W2kqeAWx/Vector-76.svg`,
      `${cdn}/9zG5NnLRSEY3W4QhoMdMss/Vector-77.svg`,
      `${cdn}/aNroW3eiF2ZiVaQmX5Dk22/Vector-78.svg`,
      `${cdn}/bv5NU8eJ94V7APfwwgftCj/Vector-79.svg`,
      `${cdn}/nVd5v1EBhnGMyvZ5bfEndD/Vector-63.svg`,
      `${cdn}/nH6hvX9UYrxJ6Kx88LgJ3H/Vector-64.svg`,
      `${cdn}/m8kF5QFGfByxMQeddtGuDY/Vector-65.svg`,
      `${cdn}/8zANd75hxqMqSYcjMHnNLv/Vector-66.svg`,
      `${cdn}/h8KBuR26Pt83zmWyGEQ6FP/Vector-67.svg`,
      `${cdn}/gj71w2sfEciCrJyynwZ4pr/Vector-68.svg`,
      `${cdn}/nDMmt93QzrXf1SZXh8ZtJN/Vector-69.svg`,
      `${cdn}/myKVYVDeLh8dgQ8iQkQ4ud/Union-3.svg`,
    ],
    stars: [
      `${cdn}/3jZKSQD7Sanhp3uT6JW4hz/Vector-60.svg`,
      `${cdn}/57jkv9RENCS57SDcSn85B1/Vector-61.svg`,
      `${cdn}/k2Hiedw3hqR6tJq3YLq8My/Vector-62.svg`,
      `${cdn}/tZoyQhJCHAZsNuaNkKLCK2/Vector-46.svg`,
      `${cdn}/nXkAZu56R674qZAWC3uho8/Vector-47.svg`,
      `${cdn}/vvpqyERujUWm4RogLWkDfJ/Vector-48.svg`,
      `${cdn}/sedamteamk697n4Bo8XbrA/Vector-49.svg`,
      `${cdn}/vzFxF5jjxD6dtnH8M2eH7o/Vector-50.svg`,
      `${cdn}/uNzLHTUJqxtXnkBs1dnVXi/Vector-51.svg`,
      `${cdn}/5u6d1YNBne8yizAjYvHeeA/Vector-52.svg`,
      `${cdn}/vGStvcxPFZeBLuDRT5CjnH/Vector-53.svg`,
      `${cdn}/q6fsNMQ2em3KGWAKbF7tBf/Vector-54.svg`,
      `${cdn}/gVYphqeqxf45qNEsvP4Fkr/Vector-55.svg`,
      `${cdn}/uvKyUz3iwFtKNh3fxzk6E2/Vector-56.svg`,
      `${cdn}/gHccCKwiNtgb2FtytHq6Cp/Vector-57.svg`,
      `${cdn}/necrdx2Rpkmd98bsqV7rMY/Vector-58.svg`,
      `${cdn}/pv2vJocksRetQJKH5xCvEu/Vector-59.svg`,
      `${cdn}/w6YH641BCvt5miaLZUcCKw/Vector-25.svg`,
      `${cdn}/ohoKv3DaowZnoeJzQ5r2Jq/Vector-27.svg`,
      `${cdn}/artUiz9r4pjsV3Q7DY3qBu/Vector-32.svg`,
      `${cdn}/a55F5MccVBJ86qxWpmGUUP/Vector-37.svg`,
      `${cdn}/ifruPn3an2q85bD2mDfxcC/Union-1.svg`,
      `${cdn}/62v5qcNs9a4rE2bwQNVgrb/Union-2.svg`,
      `${cdn}/h1dJEWChPm6UhJ9eG1X3NQ/Union.svg`,
    ],
    organics: [
      `${cdn}/rG7MALMoR7b181mqLDjwdc/Vector-72.svg`,
      `${cdn}/1vXS29c94zpfpXDDsxDELw/Vector-73.svg`,
      `${cdn}/6UEwngy9FBGUR4bjbb5dmf/Vector-75.svg`,
      `${cdn}/cDcJzbRKAaJygprjfNmDqM/Vector-40.svg`,
      `${cdn}/papBeTHWK7MauX9KNjSKVo/Vector-41.svg`,
      `${cdn}/fM5xAk7xtS2K2MbEzRUBCY/Vector-42.svg`,
      `${cdn}/pzLU4vf3bYKGUTu6p74oKP/Vector-43.svg`,
      `${cdn}/8DRBQHsFUHbCkorpLRHTiA/Vector-44.svg`,
      `${cdn}/uxpF83n7E8Hr5GCYU42Ruc/Vector-45.svg`,
      `${cdn}/oBeChiV23exHn6ANJF9NoZ/Vector-23.svg`,
      `${cdn}/u4QsLDm1y8Uugazvgtqo78/Vector-24.svg`,
      `${cdn}/qSh68VscGYCtCRq3NamUFn/Vector-26.svg`,
      `${cdn}/8RYrPv2nAGwM6WUKjwubFy/Vector-28.svg`,
      `${cdn}/i8SNFhNEWfZhqKKaqxtaiw/Vector-34.svg`,
      `${cdn}/srDk1L6aXZ2rKg5HHJGzDa/Vector-35.svg`,
      `${cdn}/mxXAHjbfThiK7a1v6SQgon/Vector-5.svg`,
      `${cdn}/bd22rrheJSG8yupg3krZ3N/Vector-7.svg`,
      `${cdn}/9RQNxdec1o9eNFVHE2BzEo/Vector-12.svg`,
      `${cdn}/nG1ppSm71mJAEhnEjNtgv7/Vector-13.svg`,
      `${cdn}/5gs22wYFTYwyAjYBPUjje9/Vector-15.svg`,
      `${cdn}/kaJmu5tY7KFdCcyqLC8o8n/Vector-17.svg`,
      `${cdn}/mhXEWTrKi3MDPqciN3PJwx/Vector-19.svg`,
      `${cdn}/4PVsFCpbNDJBhLx7wkZvd7/Subtract-1.svg`,
      `${cdn}/2HsnnZD6Tu3LeWiCsagZeN/Union-4.svg`,
    ],
  },
}

/**
 * Named picks for the semantic doodle components, so specific placements stay
 * stable while the scatter system handles variety.
 */
export const namedShapes: Record<
  DoodleFamily,
  Record<
    | 'underline'
    | 'underlineLong'
    | 'wave'
    | 'squiggle'
    | 'oval'
    | 'blob'
    | 'burst'
    | 'star'
    | 'sparkle'
    | 'arrow'
    | 'heart',
    string
  >
> = {
  lightBlue: {
    underline: `${cdn}/ne9j9zvXKDME7TufwPzfEk/Vector-70.svg`,
    underlineLong: `${cdn}/wh5VZpBYYoSubRgX7rezWA/Vector-76.svg`,
    wave: `${cdn}/j4ELnccUexPntXjZRmKCsm/Vector-74.svg`,
    squiggle: `${cdn}/9tFafPgeBVfkvgjxKens42/Vector-78.svg`,
    oval: `${cdn}/tFGvxEtT4r6eqnSmkA6yix/Vector-75.svg`,
    blob: `${cdn}/uW2xkiqU7zMnqrRcFrpuDr/Vector-72.svg`,
    burst: `${cdn}/ksqPNZVzbgccaYANbKhj45/Vector-73.svg`,
    star: `${cdn}/eDDa9yB47BMMwRqYZ3HXqw/Vector-62.svg`,
    sparkle: `${cdn}/vDTGhx9swfvyGgLXSX9roK/Vector-54.svg`,
    arrow: `${cdn}/36oFPyZ4eFiJeYS3Up1Z8C/Vector-63.svg`,
    heart: `${cdn}/ujsFE48CbEtf3hyo4EaAX5/Vector-5.svg`,
  },
  navy: {
    underline: `${cdn}/sfHz7uwoyJwCdbDuwJJmiS/Vector-70.svg`,
    underlineLong: `${cdn}/rxqhQEC3H85kQF5hkxfcis/Vector-76.svg`,
    wave: `${cdn}/5ybWCYQ6qNgxTqYFiCyrah/Vector-74.svg`,
    squiggle: `${cdn}/5odwKzagd9KCCkDjDVCJwV/Vector-78.svg`,
    oval: `${cdn}/jLFLRuvD58UQKhGkzoDw7P/Vector-75.svg`,
    blob: `${cdn}/rBfnvpwbYUDrCoeMbzUbeK/Vector-72.svg`,
    burst: `${cdn}/ga2q2wamGPwzDG12M6pkow/Vector-73.svg`,
    star: `${cdn}/hbQMmLth225YGwiZVfCJfX/Vector-62.svg`,
    sparkle: `${cdn}/9mJFKn2yW5uLtib8EAmoQH/Vector-54.svg`,
    arrow: `${cdn}/qbB1SnEZU6Y472DXp3c3WN/Vector-63.svg`,
    heart: `${cdn}/5WiwpMiNbKzvQHLo3T7SSM/Vector-5.svg`,
  },
  yellow: {
    underline: `${cdn}/1zYDThmvoZ1dcFgyhQzW8c/Vector-70.svg`,
    underlineLong: `${cdn}/oN9pC5DPrWjntgG5EgDP2a/Vector-76.svg`,
    wave: `${cdn}/joEtFZuxaj9vAFm8R7X7qs/Vector-74.svg`,
    squiggle: `${cdn}/5R7XweNSspg7jamxRPGS2X/Vector-78.svg`,
    oval: `${cdn}/dxcoGXYVmWWpUa6WPw3keD/Vector-75.svg`,
    blob: `${cdn}/c5Yxuifyxvybw53sroVYdb/Vector-72.svg`,
    burst: `${cdn}/bcaz4AADkCh6HTQyEcJ4nU/Vector-73.svg`,
    star: `${cdn}/rQE32rzXn9uh9jTbYw3nNG/Vector-62.svg`,
    sparkle: `${cdn}/phA8yazbmfoM6CJwsXX9f7/Vector-46.svg`,
    arrow: `${cdn}/cnb518MEvXkX1Mg4x8s46c/Vector-63.svg`,
    heart: `${cdn}/uL4mdsPobjTXWjh6LNcENi/Vector-5.svg`,
  },
  cream: {
    underline: `${cdn}/eTZquSPvFUqMw49uUEUtC4/Vector-70.svg`,
    underlineLong: `${cdn}/3woctDDuxdXo93W2kqeAWx/Vector-76.svg`,
    wave: `${cdn}/rsDcPpseBcKVVhfLaMhrcw/Vector-74.svg`,
    squiggle: `${cdn}/aNroW3eiF2ZiVaQmX5Dk22/Vector-78.svg`,
    oval: `${cdn}/6UEwngy9FBGUR4bjbb5dmf/Vector-75.svg`,
    blob: `${cdn}/rG7MALMoR7b181mqLDjwdc/Vector-72.svg`,
    burst: `${cdn}/1vXS29c94zpfpXDDsxDELw/Vector-73.svg`,
    star: `${cdn}/k2Hiedw3hqR6tJq3YLq8My/Vector-62.svg`,
    sparkle: `${cdn}/tZoyQhJCHAZsNuaNkKLCK2/Vector-46.svg`,
    arrow: `${cdn}/nVd5v1EBhnGMyvZ5bfEndD/Vector-63.svg`,
    heart: `${cdn}/mxXAHjbfThiK7a1v6SQgon/Vector-5.svg`,
  },
}

/**
 * Which shape colors are legible on which section background. A family is never
 * allowed on its own color — that is what made some blue shapes invisible.
 */
export type SurfaceTone = 'paper' | 'white' | 'coolLight' | 'blue' | 'navy'

export const familiesForTone: Record<SurfaceTone, DoodleFamily[]> = {
  // Warm cream page background.
  paper: ['navy', 'lightBlue', 'yellow'],
  white: ['navy', 'lightBlue', 'yellow'],
  // Very light cool blue-grey (#F4F7FB).
  coolLight: ['navy', 'yellow', 'lightBlue'],
  // Light blue panels — no light blue.
  blue: ['navy', 'cream', 'yellow'],
  // Dark navy panels — no navy.
  navy: ['cream', 'yellow', 'lightBlue'],
}
